/**
 * Which optional columns the Resource Plans list shows. Pure, client-safe, no I/O.
 *
 * "Calm first view" (docs/design-system.md): a table keeps its default columns few — the
 * identity columns (Account / Solution, Plan) plus the defaults below. The rest stay one
 * click away in the Columns menu, so nothing is removed, only tucked away.
 *
 * Account / Solution and the plan Name are always shown and are not listed here.
 */

export const PLAN_LIST_COLUMNS = [
  { key: "author", label: "Author" },
  { key: "status", label: "Status" },
  { key: "resourcingStatus", label: "Resourcing" },
  { key: "staffingNeed", label: "Staffing need" },
  { key: "pricingModel", label: "Pricing Model" },
  { key: "start", label: "Start" },
  { key: "end", label: "End" },
  { key: "totalHours", label: "Total Hours" },
  { key: "totalRevenue", label: "Total Revenue" },
  { key: "margin", label: "Margin" },
  { key: "updatedAt", label: "Last Updated" },
] as const;

export type PlanListColumn = (typeof PLAN_LIST_COLUMNS)[number]["key"];

/**
 * Start stays visible because the list's default sort is by Start. Staffing need makes this
 * eight columns, one over the 7-column first view — a product decision (2026-10-14), so
 * Resource Managers see urgency without opening the Columns menu.
 */
export const DEFAULT_PLAN_LIST_COLUMNS: readonly PlanListColumn[] = [
  "status",
  "resourcingStatus",
  "staffingNeed",
  "start",
  "totalRevenue",
  "margin",
];

function isPlanListColumn(value: unknown): value is PlanListColumn {
  return PLAN_LIST_COLUMNS.some((c) => c.key === value);
}

/**
 * A viewer's saved column choice, read back from storage. Unknown keys (a column since
 * renamed or removed) are dropped; anything unreadable returns null so the caller falls
 * back to the defaults. Returned in table order, whatever order they were saved in.
 */
export function parsePlanListColumns(raw: string | null): PlanListColumn[] | null {
  if (!raw) return null;
  let parsed: unknown;
  try {
    parsed = JSON.parse(raw);
  } catch {
    return null;
  }
  if (!Array.isArray(parsed)) return null;
  const saved = new Set(parsed.filter(isPlanListColumn));
  return PLAN_LIST_COLUMNS.map((c) => c.key).filter((key) => saved.has(key));
}

/** Show or hide one column, keeping table order. */
export function togglePlanListColumn(
  columns: readonly PlanListColumn[],
  column: PlanListColumn,
): PlanListColumn[] {
  const next = new Set(columns);
  if (next.has(column)) next.delete(column);
  else next.add(column);
  return PLAN_LIST_COLUMNS.map((c) => c.key).filter((key) => next.has(key));
}
